/**
 * Tela de confirmação de agendamento realizado - Design Premium
 * 
 * Exibe os detalhes do agendamento confirmado e a mensagem
 * de confirmação personalizada (se configurada).
 * Suporta botões de Cancelar e Reagendar para bookings vinculados a deals.
 * 
 * RELEASE: 2026-02-14
 * VERSION: 3.0.0 - Adicionado suporte a cancelar/reagendar via deal_id
 */

import { useState } from 'react';
import { stripTemplateVariables } from '@/lib/schedulingUtils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Textarea } from '@/components/ui/textarea';
import { Calendar, CheckCircle2, Clock, Video, XCircle, RefreshCw, Loader2 } from 'lucide-react';
import { format, parseISO, addMinutes } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useCancelBooking } from '@/hooks/usePublicScheduling';
import { createLogger } from '@/lib/logger';
import type { PublicEventType } from '@/hooks/usePublicScheduling';

const logger = createLogger('PublicSchedulingConfirmation');

interface PublicSchedulingConfirmationProps {
  eventType: PublicEventType;
  selectedSlot: string;
  bookingId: string;
  /** Token para cancelar o booking (presente quando há booking existente via deal) */
  cancelToken?: string;
  /** Callback ao cancelar com sucesso */
  onCancel?: () => void;
  /** Callback ao reagendar (cancela atual e volta ao fluxo) */
  onReschedule?: () => void;
}

export function PublicSchedulingConfirmation({
  eventType,
  selectedSlot,
  bookingId,
  cancelToken,
  onCancel,
  onReschedule,
}: PublicSchedulingConfirmationProps) {
  const slotDate = parseISO(selectedSlot);
  const endDate = addMinutes(slotDate, eventType.duration_minutes);
  const eventColor = eventType.color || '#3B82F6';

  const formattedDate = format(slotDate, "EEEE, dd 'de' MMMM 'de' yyyy", { locale: ptBR });
  const formattedStart = format(slotDate, 'HH:mm');
  const formattedEnd = format(endDate, 'HH:mm');

  // Estado local para controlar cancelamento
  const [isCancelled, setIsCancelled] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // Hook de cancelamento
  const cancelBookingMutation = useCancelBooking();

  /** Confirma o cancelamento do booking */
  const handleConfirmCancel = async () => {
    if (!cancelToken) return;

    try {
      logger.info('Cancelando booking via confirmação', { bookingId });
      await cancelBookingMutation.mutateAsync({
        cancelToken,
        reason: cancelReason || undefined,
      });
      setIsCancelled(true);
      setIsDialogOpen(false);
      onCancel?.();
    } catch (err) {
      logger.error('Erro ao cancelar booking', err);
    }
  };

  /** Reagendar: cancela booking atual e volta ao fluxo de seleção */
  const handleReschedule = async () => {
    if (!cancelToken) return;

    try {
      logger.info('Reagendando booking', { bookingId });
      await cancelBookingMutation.mutateAsync({
        cancelToken,
        reason: 'Reagendamento solicitado pelo visitante',
      });
      onReschedule?.();
    } catch (err) {
      logger.error('Erro ao reagendar booking', err);
    }
  };

  // =========================================================================
  // TELA DE BOOKING CANCELADO
  // =========================================================================
  if (isCancelled) {
    return (
      <div className="p-6 sm:p-10 text-center">
        {/* Ícone de cancelamento */}
        <div className="flex h-18 w-18 items-center justify-center rounded-full mx-auto mb-6 animate-in zoom-in-50 duration-500 bg-destructive/10">
          <XCircle className="h-9 w-9 text-destructive" />
        </div>

        <h2 className="text-2xl font-bold text-foreground mb-2 tracking-tight">
          Agendamento cancelado
        </h2>
        <p className="text-muted-foreground mb-8 text-sm max-w-sm mx-auto">
          Seu agendamento foi cancelado com sucesso.
        </p>

        {/* Botão para fazer novo agendamento */}
        {onReschedule && (
          <Button
            variant="outline"
            onClick={onReschedule}
            className="gap-2"
          >
            <RefreshCw className="h-4 w-4" />
            Fazer novo agendamento
          </Button>
        )}
      </div>
    );
  }

  // =========================================================================
  // TELA DE CONFIRMAÇÃO PADRÃO
  // =========================================================================
  return (
    <div className="p-6 sm:p-10 text-center">
      {/* Ícone de sucesso com animação scale-in */}
      <div
        className="flex h-18 w-18 items-center justify-center rounded-full mx-auto mb-6 animate-in zoom-in-50 duration-500"
        style={{ backgroundColor: `${eventColor}12` }}
      >
        <CheckCircle2 className="h-9 w-9" style={{ color: eventColor }} />
      </div>

      {/* Título */}
      <h2 className="text-2xl font-bold text-foreground mb-2 tracking-tight">
        {eventType.requires_approval
          ? 'Agendamento solicitado!'
          : 'Agendamento confirmado!'}
      </h2>
      <p className="text-muted-foreground mb-8 text-sm max-w-sm mx-auto">
        {eventType.requires_approval
          ? 'Seu agendamento foi enviado e aguarda aprovação. Você receberá uma confirmação por email.'
          : 'Seu agendamento foi confirmado com sucesso. Verifique seu email para mais detalhes.'}
      </p>

      {/* Card de detalhes com borda lateral colorida */}
      <div
        className="bg-muted/40 border border-border/40 rounded-xl p-6 text-left max-w-sm mx-auto space-y-4 border-l-4"
        style={{ borderLeftColor: eventColor }}
      >
        {/* Tipo de evento */}
        <div>
          <p className="text-[11px] text-muted-foreground uppercase tracking-widest mb-1 font-semibold">
            Evento
          </p>
          <p className="font-semibold text-foreground">{stripTemplateVariables(eventType.title)}</p>
        </div>

        {/* Data e hora */}
        <div>
          <p className="text-[11px] text-muted-foreground uppercase tracking-widest mb-1 font-semibold">
            Quando
          </p>
          <div className="space-y-1.5">
            <p className="text-sm text-foreground capitalize flex items-center gap-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              {formattedDate}
            </p>
            <p className="text-sm text-foreground flex items-center gap-2">
              <Clock className="h-4 w-4 text-muted-foreground" />
              {formattedStart} - {formattedEnd} ({eventType.duration_minutes} min)
            </p>
          </div>
        </div>

        {/* Conferência */}
        {eventType.conferencing_provider === 'google_meet' && (
          <div>
            <p className="text-[11px] text-muted-foreground uppercase tracking-widest mb-1 font-semibold">
              Conferência
            </p>
            <p className="text-sm text-foreground flex items-center gap-2">
              <Video className="h-4 w-4 text-muted-foreground" />
              Google Meet (link será enviado por email)
            </p>
          </div>
        )}

        {/* Status */}
        <div className="pt-1">
          <Badge
            variant="outline"
            className={
              eventType.requires_approval
                ? 'bg-amber-500/10 text-amber-600 border-amber-500/30'
                : 'bg-emerald-500/10 text-emerald-600 border-emerald-500/30'
            }
          >
            {eventType.requires_approval ? 'Aguardando aprovação' : 'Confirmado'}
          </Badge>
        </div>
      </div>

      {/* Mensagem de confirmação personalizada */}
      {eventType.confirmation_message && (
        <div
          className="mt-6 p-4 rounded-xl text-sm text-foreground max-w-sm mx-auto border"
          style={{
            backgroundColor: `${eventColor}06`,
            borderColor: `${eventColor}15`,
          }}
        >
          {eventType.confirmation_message}
        </div>
      )}

      {/* Botões Cancelar e Reagendar - exibidos quando há cancelToken (deal booking) */}
      {cancelToken && (
        <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3 max-w-sm mx-auto">
          {/* Botão Reagendar */}
          <Button
            variant="outline"
            className="w-full sm:w-auto gap-2"
            onClick={handleReschedule}
            disabled={cancelBookingMutation.isPending}
          >
            {cancelBookingMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            Reagendar
          </Button>

          {/* Botão Cancelar com diálogo de confirmação */}
          <AlertDialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <AlertDialogTrigger asChild>
              <Button
                variant="outline"
                className="w-full sm:w-auto gap-2 text-destructive border-destructive/30 hover:bg-destructive/10 hover:text-destructive"
                disabled={cancelBookingMutation.isPending}
              >
                <XCircle className="h-4 w-4" />
                Cancelar agendamento
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Cancelar agendamento?</AlertDialogTitle>
                <AlertDialogDescription>
                  Tem certeza que deseja cancelar seu agendamento de{' '}
                  <strong>{stripTemplateVariables(eventType.title)}</strong> em{' '}
                  <strong>{formattedDate}</strong> às <strong>{formattedStart}</strong>?
                  Esta ação não pode ser desfeita.
                </AlertDialogDescription>
              </AlertDialogHeader>

              {/* Campo opcional de motivo */}
              <div className="py-2">
                <Textarea
                  placeholder="Motivo do cancelamento (opcional)"
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  className="resize-none"
                  rows={3}
                />
              </div>

              <AlertDialogFooter>
                <AlertDialogCancel>Voltar</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleConfirmCancel}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  disabled={cancelBookingMutation.isPending}
                >
                  {cancelBookingMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : null}
                  Confirmar cancelamento
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      )}
    </div>
  );
}
